import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStylePreview } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLModal } from '../../common/views/modals/SMLModal/SMLModal';
import { transformImageUrl } from '../../music24/components/SMLImage/SMLImage';
import { stylePreviewMessages as messages } from './stylePreviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The CSS width of the picture on a card (imgproxy makes it 3× for a sharp screen). */
const CARD_WIDTH_PX = 360;
/** The CSS width of the larger picture. */
const LARGE_WIDTH_PX = 960;

interface StylePreviewPictureProps {
  /** The preview of the style, or undefined when the style has none. */
  preview: DirectedStylePreview | undefined;
  /** The title of the style, for the alt text. */
  title: string;
}

/**
 * The top of a style card (owner 2026-10-19): the preview still of the style. While the image model
 * makes it, a box with a spinner. A ready picture opens larger on a tap. No preview, a failed one or
 * a picture that does not load: nothing, so the card never shows an empty box.
 */
const StylePreviewPictureComponent: React.FC<StylePreviewPictureProps> = ({ preview, title }) => {
  const intl = useIntl();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  if (preview === undefined || preview.status === 'failed') {
    return null;
  }
  if (preview.status === 'working') {
    return (
      <div className={clsx(styles.look, styles.lookWorking)} role="status" data-testid="style-preview-working">
        <span className={styles.lookSpinner} aria-hidden />
        <span className={styles.lookWorkingText}>{intl.formatMessage(messages.making)}</span>
      </div>
    );
  }
  const url = preview.imageUrl;
  if (url === null || url === failedUrl) {
    return null;
  }
  const alt = intl.formatMessage(messages.alt, { style: title });
  return (
    <>
      <button
        type="button"
        className={clsx(styles.look, styles.lookButton)}
        aria-label={intl.formatMessage(messages.open, { style: title })}
        onClick={() => setIsOpen(true)}
        data-testid="style-preview"
      >
        <img src={transformImageUrl(url, CARD_WIDTH_PX)} alt={alt} onError={() => setFailedUrl(url)} />
      </button>
      <SMLModal isOpen={isOpen} onClosed={() => setIsOpen(false)} size="xl" customModal ariaLabel={alt}>
        <img className={styles.lookLarge} src={transformImageUrl(url, LARGE_WIDTH_PX)} alt={alt} />
      </SMLModal>
    </>
  );
};

export const StylePreviewPicture = TypedMemo(StylePreviewPictureComponent);
